import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './ActiveEndorsements.scss';
import Button from '../Buttons/DefaultButton/button';
import Modal from 'react-modal';
import Icon from '../Icon';
import Navigator from '../../Navigation/Navigator';
import { getToken } from '../../Session/SessionRedux';
import { connect } from 'react-redux';
import ApplicationAction, {
   isFetchingApplication,
   isFetchingApplicationSuccess,
   saveApplicationRequest
} from 'pgdb-data-layer/lib/Redux/ApplicationsRedux';
import { APPLICATION_ENDORSEMENT, ENDORSEMENT_TYPE_PLUMBER, ENDORSEMENT_TYPE_DRAINLAYER, APPLICATION_ENDORSEMENT_RENEWAL} from 'pgdb-data-layer/lib/Helpers/Endorsement';
import { APPLICATION_REGISTRATION_DRAFT, APPLICATION_REGISTRATION_ON_HOLD_PENDING_PAYMENT, APPLICATION_STATUS_PENDING_TRAINING } from 'pgdb-data-layer/lib/Constants/Contact';

const FEE_STATUS_PAID = 'PAID';

const ActiveEndorsements = ({ applicationEndorsements, deleteApplication, token, saveApplicationRequest, getApplicationPayload, applicationPayload }) => {
   const [deleteModalOpen, setDeleteModalOpen] = useState(false);
   const [selectedApplicationId, setSelectedApplicationId] = useState(null);
   const [selectedApplicationTypeCode, setSelectedApplicationTypeCode] = useState(null);
   const [paidApplicationIds, setPaidApplicationIds] = useState(new Set());
   const [checkingIds, setCheckingIds] = useState(new Set());
   const checkedIdsRef = useRef(new Set());

   useEffect(() => {
      if (!applicationPayload || applicationPayload.length === 0) return;
      applicationPayload.forEach(app => {
         checkedIdsRef.current.add(app.applicationId);
         if (app.feeRequestStatusCode === FEE_STATUS_PAID) {
            setPaidApplicationIds(prev => new Set([...prev, app.applicationId]));
         }
      });
      setCheckingIds(prev => {
         const next = new Set(prev);
         applicationPayload.forEach(app => next.delete(app.applicationId));
         return next;
      });
   }, [applicationPayload]);

   useEffect(() => {
      if (!applicationEndorsements || !token) return;
      const toCheck = applicationEndorsements.filter(e =>
         e.applicationStatusCode === APPLICATION_REGISTRATION_ON_HOLD_PENDING_PAYMENT &&
         !checkedIdsRef.current.has(e.applicationId)
      );
      if (toCheck.length === 0) return;
      setCheckingIds(prev => new Set([...prev, ...toCheck.map(e => e.applicationId)]));
      toCheck.forEach(e => getApplicationPayload(token, e.applicationId));
   }, [applicationEndorsements, token]);

   if (!applicationEndorsements || applicationEndorsements.length === 0) {
      return <></>;
   }
   const getEndorsementType = (code) => code === ENDORSEMENT_TYPE_PLUMBER ? 'Plumber' : 'Drainlayer';

   const handleDeleteClick = (applicationId, applicationTypeCode) => {
      setSelectedApplicationId(applicationId);
      setSelectedApplicationTypeCode(applicationTypeCode);
      setDeleteModalOpen(true);
   };

   const handleConfirmDelete = () => {
      deleteApplication(token, selectedApplicationId);
      setDeleteModalOpen(false);
      setSelectedApplicationId(null);
      setSelectedApplicationTypeCode(null);
   };

   const handleCancelDelete = () => {
      setDeleteModalOpen(false);
      setSelectedApplicationId(null);
      setSelectedApplicationTypeCode(null);
   };

   const navigation = (status, applicationId, applicationTypeCode) => {
      const isRenewal = applicationTypeCode === APPLICATION_ENDORSEMENT_RENEWAL;
      switch(status) {
         case APPLICATION_REGISTRATION_DRAFT:
            if (isRenewal) {
               Navigator.toRenewEndorsementEdit(applicationId);
            } else {
               Navigator.toApplyEndorsementEdit(applicationId);
            }
            break;
         case APPLICATION_STATUS_PENDING_TRAINING:
            if (isRenewal) {
               Navigator.toRenewEndorsementTraining(applicationId);
            } else {
               Navigator.toEndorsementTraining(applicationId);
            }
            break;
         case APPLICATION_REGISTRATION_ON_HOLD_PENDING_PAYMENT:
            if (isRenewal) {
               Navigator.toRenewEndorsementPayment(applicationId);
            } else {
               Navigator.toEndorsementPayment(applicationId);
            }
            break;
         default:
            alert('Cannot continue endorsement application with status ' + status);
      }
   }

   const getStatusLabel = (endorsement) => {
      if (checkingIds.has(endorsement.applicationId)) {
         return '-';
      }
      if (
         endorsement.applicationStatusCode === APPLICATION_REGISTRATION_ON_HOLD_PENDING_PAYMENT &&
         paidApplicationIds.has(endorsement.applicationId)
      ) {
         return 'Paid';
      }
      return endorsement.applicationStatusDescription || '-';
   };

   return (<>
         <div className="row">
            <div className="col s10 m10 l10 offset-l1">
               <h4 className="columnheader" id="endorsement-section-header">
                  Your Endorsement Applications
               </h4>
            </div>
         </div>
         <div className="row">
            <div className="col l10 m12 offset-l1 s12 header-column">
               <div className="col s4 m4 l4 center-align">Type</div>
               <div className="col s4 m4 l4 center-align">Status</div>
               <div className="col s4 m4 l4 center-align">Action</div>
            </div>
         </div>
         {applicationEndorsements.map((endorsement) => (
            <div className="row" key={endorsement.applicationId}>
               <div className='endorsement-list-section'>
                  <div className="col l10 m12 offset-l1 s12 header-column">
                     <div className="endorsement-status-row">
                           <div className="row m-0 endorsement-status-details">
                              <p className="col s4 m4 l4 center-align">
                                 {endorsement.endorsementTypeCodes.length > 1 ? 'Plumber and Drainlayer' : getEndorsementType(endorsement.endorsementTypeCodes[0]) }
                              </p>
                              <p className="col s4 m4 l4 center-align">
                                 {getStatusLabel(endorsement)}
                              </p>
                              <div className="col s4 m4 l4 center-align endorsement-action-buttons">
                                 <Button
                                    size="btn-small"
                                    type="secondary"
                                    className="waves-effect waves-light draft-action-buttons"
                                    id={`delete-endorsement-${endorsement.applicationId}`}
                                    text={'Delete'}
                                    onClick={() => handleDeleteClick(endorsement.applicationId, endorsement.applicationTypeCode)}
                                 />
                                 <Button
                                    size="btn-small"
                                    type="primary"
                                    className={`action-buttons draft-action-buttons `}
                                    id={`continue-endorsement-${endorsement.endorsementId}`}
                                    text={'Continue'}
                                    onClick={()=>navigation(endorsement.applicationStatusCode,endorsement.applicationId,endorsement.applicationTypeCode)}
                                 />
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         ))}
         <Modal
            isOpen={deleteModalOpen}
            onRequestClose={handleCancelDelete}
            className="delete-modal"
            overlayClassName="generic-modal-overlay"
            appElement={document.getElementById('root')}
         >
            <Icon
               className="fit-model-icon material-icons right"
               iconName="close"
               onClick={handleCancelDelete}
            />
            <br />
            <div className="fit-modal-body">
               <h4>
                  {selectedApplicationTypeCode === APPLICATION_ENDORSEMENT_RENEWAL
                     ? 'Are you sure you want to delete this endorsement renewal?'
                     : 'Are you sure you want to delete this application?'}
               </h4>
               <p>
                  {selectedApplicationTypeCode === APPLICATION_ENDORSEMENT_RENEWAL
                     ? 'Are you sure you want to delete this draft endorsement renewal? This process cannot be undone.'
                     : 'Are you sure you want to delete this draft application? This process cannot be undone.'}
               </p>
            </div>
            <div className="delete-application-buttons">
               <button
                  type="button"
                  className="modal-cancel-button"
                  onClick={handleCancelDelete}
               >
                  NO
               </button>
               <button
                  type="button"
                  className="modal-delete-button"
                  onClick={handleConfirmDelete}
               >
                  YES
               </button>
            </div>
         </Modal>
      </>
   );
};

ActiveEndorsements.propTypes = {
   applicationEndorsements: PropTypes.array,
   deleteApplication: PropTypes.func.isRequired,
   token: PropTypes.string.isRequired,
 };

ActiveEndorsements.defaultProps = {
   applicationEndorsements: [],
};

const mapStateToProps = state => ({
   token: getToken(state),
   applicationPayload: state.application.payload ? state.application.payload.applications || [] : [],
});

const mapDispatchToProps = {
   saveApplicationRequest: ApplicationAction.saveApplicationRequest,
   deleteApplication: ApplicationAction.deleteApplicationRequest,
   getApplicationPayload: ApplicationAction.getApplicationPayloadRequest,
};


export default connect(mapStateToProps, mapDispatchToProps)(ActiveEndorsements);
// trigger build pipeline